// ============================================================
// components/HomePage.tsx
//
// Web home ("/"): a marketing page that sends people to the
// iPhone app, where the full STASH experience lives. The web
// app is a companion, so the secondary action is web sign-in
// (signed out) or the STASH Board (signed in).
// ============================================================
import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Camera,
  MessageCircleQuestionMark,
  ShieldCheck,
  Smartphone,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { RootState } from '../store';
import { AppStoreBadge, AppStoreQr, BestOnIPhone } from './landing/BestOnIPhone';
import { Reveal } from './common/Reveal';

// Staggered entrance for each section; nothing moves for reduced-motion users.
const rise =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-6 motion-safe:duration-700 motion-safe:fill-mode-both';

function SecondaryAction({ signedIn }: { signedIn: boolean }): React.JSX.Element {
  const to = signedIn ? '/app/stashboard' : '/app/login';
  const label = signedIn ? 'Open your STASH Board' : 'Sign in on the web';
  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-1.5 rounded-full px-4 py-3 text-sm font-semibold text-[var(--stash-blue)] transition-colors hover:bg-[var(--stash-blue)]/10"
    >
      {label}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
    </Link>
  );
}

/** Decorative recommendation chip floating around the hero phones. */
function TrustChip({
  initials,
  tint,
  title,
  detail,
  className,
  delay,
}: {
  initials: string;
  tint: string;
  title: string;
  detail: string;
  className: string;
  delay: string;
}): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={`absolute flex items-center gap-2.5 rounded-2xl border border-border/60 bg-card/85 px-3 py-2 shadow-lg backdrop-blur-md motion-safe:animate-stash-float ${className}`}
      style={{ animationDelay: delay }}
    >
      <span
        className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-bold text-white"
        style={{ background: `var(${tint})` }}
      >
        {initials}
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-sm font-semibold text-foreground">{title}</span>
        <span className="text-xs font-medium text-muted-foreground">{detail}</span>
      </span>
    </div>
  );
}

interface Feature {
  icon: LucideIcon;
  tint: string;
  title: string;
  body: string;
}

const FEATURES: Feature[] = [
  {
    icon: Camera,
    tint: '--stash-blue',
    title: 'Snap & Stash',
    body: 'Photograph a business card, a work truck or a storefront. STASH reads the details and saves the contact for you.',
  },
  {
    icon: Users,
    tint: '--stash-pink',
    title: 'Your inner circle',
    body: 'See the plumbers, sitters and mechanics your friends, family and micro-communities actually use.',
  },
  {
    icon: MessageCircleQuestionMark,
    tint: '--stash-teal',
    title: 'Ask STASH',
    body: '“Who fixed our AC last summer?” Ask in plain language and get an answer from your stash and your circle’s.',
  },
  {
    icon: Smartphone,
    tint: '--stash-green',
    title: 'Everywhere on iPhone',
    body: 'Your StashCards show up in Contacts, Messages, Mail and Spotlight, without cluttering your personal contacts.',
  },
];

const STEPS = [
  { title: 'Snap it', body: 'Save the pros you use, with a photo and a note about the job.' },
  { title: 'Share with your circle', body: 'Friends and your micro-communities see who you trust.' },
  { title: 'Ask when it matters', body: 'Need a sitter by Friday? Your people have already answered.' },
];

function SectionHeading({
  id,
  eyebrow,
  title,
  lede,
}: {
  id: string;
  eyebrow: string;
  title: string;
  lede?: string;
}): React.JSX.Element {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <p className="m-0 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--stash-blue)]">{eyebrow}</p>
      <h2 id={id} className="mt-3 mb-0 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
        {title}
      </h2>
      {lede && <p className="mt-4 mb-0 text-base leading-relaxed text-muted-foreground">{lede}</p>}
    </div>
  );
}

function HomePage(): React.JSX.Element {
  const signedIn = useSelector((state: RootState) => Boolean(state.auth.currentUser));

  return (
    <div className="flex flex-col gap-16 pb-16 font-sans md:gap-24">
      {/* ── Hero (full width) ────────────────────────────────── */}
      <section aria-labelledby="home-hero-title" className="relative isolate overflow-hidden border-b border-border/40">
        {/* Atmosphere: soft brand glows */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-[var(--stash-blue)]/25 blur-3xl" />
          <div className="absolute -right-16 top-10 h-80 w-80 rounded-full bg-[var(--stash-pink)]/30 blur-3xl" />
          <div className="absolute -bottom-40 left-1/3 h-96 w-96 rounded-full bg-[var(--stash-teal)]/20 blur-3xl" />
        </div>

        <div className="mx-auto grid w-full max-w-6xl items-center gap-12 px-4 py-16 md:grid-cols-[1.1fr_0.9fr] md:px-6 md:py-24">
          <div className={`flex flex-col items-start ${rise}`}>
            <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/60 px-3.5 py-1.5 text-xs font-semibold text-muted-foreground backdrop-blur">
              <ShieldCheck className="h-3.5 w-3.5 text-[var(--stash-blue)]" aria-hidden="true" />
              Trusted recommendations from people you know
            </span>

            <h1
              id="home-hero-title"
              className="mt-6 mb-0 text-5xl font-bold leading-[1.02] tracking-tight text-foreground md:text-6xl lg:text-7xl"
            >
              The people you trust,{' '}
              <span className="bg-gradient-to-r from-[var(--stash-blue)] via-[color-mix(in_oklch,var(--stash-pink),var(--foreground)_35%)] to-[color-mix(in_oklch,var(--stash-teal),var(--foreground)_25%)] bg-clip-text text-transparent">
                in your pocket.
              </span>
            </h1>

            <p className="mt-6 mb-0 max-w-xl text-lg leading-relaxed text-muted-foreground">
              The plumber who showed up on a Sunday. The sitter your sister swears by. STASH keeps the people you and your
              friends rely on, so the next time you need someone, you already know who to call.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <AppStoreBadge className="h-14" />
              <SecondaryAction signedIn={signedIn} />
            </div>
            <p className="mt-4 mb-0 text-xs font-medium text-muted-foreground">
              Free on iPhone and iPad · United States and Canada
            </p>
          </div>

          {/* Product shot with a constellation of trust */}
          <div className={`relative mx-auto w-full max-w-sm ${rise} motion-safe:delay-200`}>
            <div
              aria-hidden="true"
              className="absolute inset-x-6 top-10 bottom-6 -z-10 rounded-full bg-gradient-to-b from-[var(--stash-blue)]/40 via-[var(--stash-pink)]/25 to-transparent blur-2xl"
            />
            <img
              src="/2026-stash-two-phones.png"
              alt="The STASH app on two iPhones: a vendor’s StashCard and a STASH Board of categories"
              width={400}
              height={697}
              fetchPriority="high"
              className="mx-auto h-auto w-64 drop-shadow-2xl motion-safe:animate-stash-float md:w-72"
            />
            <TrustChip
              initials="RP"
              tint="--stash-blue"
              title="Rivera Plumbing"
              detail="Stashed by 3 friends"
              className="left-0 top-16 md:-left-10"
              delay="0.6s"
            />
            <TrustChip
              initials="JH"
              tint="--stash-pink"
              title="Juniper Hair"
              detail="Priya’s go-to"
              className="right-0 top-1/2 md:-right-8"
              delay="1.4s"
            />
            <div
              aria-hidden="true"
              className="absolute left-0 bottom-10 max-w-[13rem] rounded-2xl rounded-bl-md bg-[var(--stash-blue)] px-4 py-2.5 text-sm font-semibold text-white shadow-lg motion-safe:animate-stash-float md:-left-12"
              style={{ animationDelay: '2.2s' }}
            >
              Who do my friends use for car repair?
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-16 px-4 md:gap-24 md:px-6">
        {/* ── Best on iPhone ───────────────────────────────────── */}
        <Reveal>
          <BestOnIPhone />
        </Reveal>

        {/* ── Features ─────────────────────────────────────────── */}
        <Reveal>
          <section aria-labelledby="home-features-title">
            <SectionHeading
              id="home-features-title"
              eyebrow="Why STASH"
              title="Find someone good, the way you always have"
              lede="Word of mouth, organized. Every pro in STASH comes from someone you know, not an anonymous review."
            />
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {FEATURES.map(({ icon: Icon, tint, title, body }) => (
                <article
                  key={title}
                  className="group rounded-2xl border border-border/60 bg-card p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <span
                    className="grid h-11 w-11 place-items-center rounded-xl"
                    style={{
                      background: `color-mix(in oklch, var(${tint}) 18%, transparent)`,
                      // Deepen pale brand tints on light backgrounds (and lift them on dark).
                      color: `color-mix(in oklch, var(${tint}), var(--foreground) 30%)`,
                    }}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 mb-0 text-lg font-bold tracking-tight text-foreground">{title}</h3>
                  <p className="mt-2 mb-0 text-sm leading-relaxed text-muted-foreground">{body}</p>
                </article>
              ))}
            </div>
          </section>
        </Reveal>

        {/* ── How it works ─────────────────────────────────────── */}
        <Reveal>
          <section aria-labelledby="home-how-title">
            <SectionHeading id="home-how-title" eyebrow="How it works" title="Three steps to never guessing again" />
            <ol className="relative mt-10 grid list-none gap-6 p-0 md:grid-cols-3">
              <div
                aria-hidden="true"
                className="absolute top-6 right-[16%] left-[16%] hidden border-t-2 border-dashed border-border md:block"
              />
              {STEPS.map((step, i) => (
                <li key={step.title} className="relative flex flex-col items-center text-center">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-[var(--stash-blue)] text-lg font-bold text-white shadow-md ring-8 ring-background">
                    {i + 1}
                  </span>
                  <h3 className="mt-4 mb-0 text-lg font-bold tracking-tight text-foreground">{step.title}</h3>
                  <p className="mt-2 mb-0 max-w-xs text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                </li>
              ))}
            </ol>
          </section>
        </Reveal>

        {/* ── Trust band ───────────────────────────────────────── */}
        <Reveal>
          <section
            aria-label="Our promise"
            className="flex flex-col items-center gap-4 rounded-[2rem] border border-border/60 bg-[var(--stash-surface-sunken,var(--muted))] px-6 py-10 text-center"
          >
            <p className="m-0 max-w-2xl text-2xl font-bold tracking-tight text-foreground md:text-3xl">
              No anonymous reviews. No paid rankings.{' '}
              <span className="text-[var(--stash-blue)]">Just the people you know.</span>
            </p>
            <ul className="m-0 flex list-none flex-wrap justify-center gap-2 p-0">
              {['Recommendations from your circle', 'Businesses can’t pay to rank', 'Your notes, your history'].map(
                (pill) => (
                  <li
                    key={pill}
                    className="rounded-full border border-border/60 bg-card px-4 py-1.5 text-xs font-semibold text-muted-foreground"
                  >
                    {pill}
                  </li>
                ),
              )}
            </ul>
          </section>
        </Reveal>

        {/* ── Closing call to action ───────────────────────────── */}
        <Reveal>
          <section
            aria-labelledby="home-cta-title"
            className="relative isolate overflow-hidden rounded-[2rem] px-6 py-16 text-center md:py-20"
          >
            <div
              aria-hidden="true"
              className="absolute inset-0 -z-10 bg-gradient-to-br from-[var(--stash-blue)]/20 via-[var(--stash-pink)]/20 to-[var(--stash-teal)]/20"
            />
            <div
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 -z-10 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--stash-blue)]/25 blur-3xl"
            />
            <img
              src="/stash-app-icon.png"
              alt=""
              width={1024}
              height={1024}
              loading="lazy"
              decoding="async"
              className="mx-auto h-20 w-20 drop-shadow-xl"
            />
            <h2
              id="home-cta-title"
              className="mx-auto mt-6 mb-0 max-w-2xl text-4xl font-bold tracking-tight text-foreground md:text-5xl"
            >
              Trusted by the people you trust.
            </h2>
            <p className="mx-auto mt-4 mb-0 max-w-xl text-base leading-relaxed text-muted-foreground">
              Start your stash in a minute. Snap the first pro you love, and invite the friends whose picks you already
              follow.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <AppStoreBadge className="h-14" />
              <AppStoreQr />
            </div>
            <div className="mt-4">
              <SecondaryAction signedIn={signedIn} />
            </div>
          </section>
        </Reveal>
      </div>
    </div>
  );
}

export default HomePage;
